import api from '@/api';
import { ColumnField } from '@/api/caseTable/ColumnField';
import { ASSET_ID_COLUMN, EtisColumn } from '@/api/caseTable/EtisArrestsExport';
import { Filters } from '@/api/caseTable/Filters';
import { MultiSortMeta } from '@/types/DataTable/MultiSortMeta';

const FIRST_ASSET_ID = 1;

const DELIMITER = ';';

/**
 * Cases must be requested in the same order the back-end orders the
 * arrests of each page by, so that rows stay ordered across pages.
 *
 * NB: 1 is ascending for back-end, although Order enum names it otherwise.
 */
const SORT_BY_CASE_NUMBER: MultiSortMeta[] = [
  { field: ColumnField.NUMBER, order: 1 },
];

/**
 * Creates both header lines of ETIS import file: the first line with
 * column groups (f.e "Vara") and the second one with column names
 * (f.e "KIS_AvalduseID").
 */
function toHeader(columns: EtisColumn[]): string {
  return (
    [
      columns.map((column) => column.group).join(DELIMITER),
      columns.map((column) => column.name).join(DELIMITER),
    ].join('\n') + '\n'
  );
}

/**
 * Creates a line of ETIS import file from values of a single arrest, in
 * the order of given columns. VaraId is set from given asset id and values
 * not returned by back-end are left empty.
 */
function toLine(
  columns: EtisColumn[],
  values: Record<string, string>,
  assetId: number
): string {
  return (
    columns
      .map(({ name }) =>
        name === ASSET_ID_COLUMN ? String(assetId) : values[name] ?? ''
      )
      .join(DELIMITER) + '\n'
  );
}

/**
 * Function for fetching active arrests of cases found by given filters as
 * ETIS import files "Varad" and "Arestid". Both files are required by ETIS
 * and their rows are linked together by VaraId column.
 *
 * Arrests are fetched for 1000 cases at a time and a progress function passed
 * can be used to display the current progress compared to all pages needed.
 * Columns of both files are defined by back-end and returned with each page.
 *
 * @param filters Filters to use to fetch cases.
 * @param itemsTotal Total number of cases to be found.
 * @param progress Function to update progress of fetching arrests.
 * @returns Contents of both csv files as text.
 */
export async function fetchAllArrestsAsEtisCsv(
  filters: Filters,
  itemsTotal: number,
  progress: (p: number, t: number) => void
): Promise<{ assets: string; arrests: string }> {
  const itemsPerPage = 1000;
  // At least the first page is always fetched, since the columns needed
  // for header rows are returned by back-end even if no cases are found.
  const pages = Math.max(1, Math.ceil(itemsTotal / itemsPerPage));

  let assets = '';
  let arrests = '';
  let assetId = FIRST_ASSET_ID;

  // Pages must be fetched one by one to keep VaraId values in order.
  for (let page = 1; page <= pages; page++) {
    const { columns, rows } = await api.caseTable.fetchEtisArrestsExport({
      columns: [],
      limit: itemsPerPage,
      page,
      sort: SORT_BY_CASE_NUMBER,
      // Arrests of other bailiffs must never be exported, so cases of all
      // bailiffs are never requested. Back-end enforces this as well.
      filters: { ...filters, includeAllBailiffs: false },
    });

    if (page === 1) {
      assets = toHeader(columns.vara);
      arrests = toHeader(columns.arest);
    }

    for (const row of rows) {
      assets += toLine(columns.vara, row.vara, assetId);
      arrests += toLine(columns.arest, row.arest, assetId);
      assetId++;
    }

    progress(page, pages);
  }

  return { assets, arrests };
}
